import React, { useState, useEffect } from 'react';
import { SearchIcon, CloseIcon } from '@/components/icons';
import { Button } from '@/components/ui';
import { useFilterGalleries, useHomePageHighlights, useUpdateHomePageHighlights, useSearchGalleries } from '@/lib/api/hooks/gallery';
import { showToast } from '@/lib/toast';
import { WebsitePageSection } from '@/types';
import { GalleryFolderResponse, GalleryTitleResponse, GalleryHighlight } from '@/lib/api/types';
import { X } from 'lucide-react';

// Helper function to format date
const formatPublishedDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return '';
    }
};

interface InlinePhotoGalleryEditorProps {
    data: WebsitePageSection;
    onSave: (data: Partial<WebsitePageSection>) => void;
    onCancel: () => void;
}

// Extended type for gallery items with folder info
interface GalleryItem extends GalleryTitleResponse {
    folderName: string;
    galleryId: string;
}

const InlinePhotoGalleryEditor: React.FC<InlinePhotoGalleryEditorProps> = ({
    onCancel,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [selectedGalleries, setSelectedGalleries] = useState<GalleryItem[]>([]);

    const { data: allGalleriesData, isLoading: isAllLoading } = useFilterGalleries(
        { page: 1, size: 20 },
        {}
    );

    const { data: searchResultsData, isLoading: isSearchLoading } = useSearchGalleries(
        { page: 1, size: 20 },
        searchQuery,
        !!searchQuery
    );

    const rawGalleries = searchQuery
        ? (searchResultsData?.data?.data || [])
        : (allGalleriesData?.data?.data || []);

    // Flatten galleries from folder structure
    const allGalleryItems: GalleryItem[] = rawGalleries.flatMap((folder: GalleryFolderResponse) =>
        folder.titleResponseList.map((gallery: GalleryTitleResponse) => ({
            ...gallery,
            folderName: folder.folderName,
            galleryId: gallery.photoGalleryId
        }))
    );

    // Filter out already selected galleries
    const galleries = allGalleryItems.filter(g => !selectedIds.includes(g.galleryId));

    const isLoading = searchQuery ? isSearchLoading : isAllLoading;

    const { data: highlightsData } = useHomePageHighlights('HOME_PAGE_IS_MULTIMEDIA');
    const updateHighlightsMutation = useUpdateHomePageHighlights();

    useEffect(() => {
        if (highlightsData?.data) {
            const currentIds = highlightsData.data.map((gallery: GalleryHighlight) => gallery.galleryId);
            setSelectedIds(currentIds);
            // Map highlight data to GalleryItem format
            const selectedItems: GalleryItem[] = highlightsData.data.map((g: GalleryHighlight) => ({
                photoGalleryId: g.galleryId,
                galleryId: g.galleryId,
                title: g.title,
                photoUrl: g.photoUrl,
                folderName: g.folderName || 'Unknown',
                datePublished: g.datePublished,
                publishedByName: g.publishedByName
            }));
            setSelectedGalleries(selectedItems);
        }
    }, [highlightsData]);

    const handleToggleSelect = (galleryId: string) => {
        const isAlreadySelected = selectedIds.includes(galleryId);

        if (isAlreadySelected) {
            setSelectedIds(prev => prev.filter(id => id !== galleryId));
            setSelectedGalleries(prev => prev.filter(g => g.galleryId !== galleryId));
        } else {
            if (selectedIds.length >= 4) {
                showToast.error("You can only select up to 4 galleries.");
                return;
            }
            const galleryToAdd = allGalleryItems.find(g => g.galleryId === galleryId);
            if (galleryToAdd) {
                setSelectedIds(prev => [...prev, galleryId]);
                setSelectedGalleries(prev => [...prev, galleryToAdd]);
            }
        }
    };

    const handleSave = async () => {
        try {
            await updateHighlightsMutation.mutateAsync({ photoGalleryIds: selectedIds, type: 'HOME_PAGE_IS_MULTIMEDIA' });
            showToast.success('Photo gallery highlights updated successfully');
            onCancel();
        } catch (error) {
            console.error('Failed to update highlights:', error);
            showToast.error('Failed to update highlights');
        }
    };

    const defaultImage = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-11_sxxnhn.png";

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="font-semibold text-lg text-gray-900">Select Photo Galleries</h3>
                <button
                    onClick={onCancel}
                    className="text-gray-500 hover:text-gray-700 p-1"
                >
                    <CloseIcon className="w-5 h-5" />
                </button>
            </div>

            {selectedGalleries.length > 0 && (
                <div className="p-4 border-b border-gray-200 bg-wine-50/30">
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-700">Currently Selected ({selectedGalleries.length}/4)</h4>
                    </div>
                    <div className="space-y-2">
                        {selectedGalleries.map((gallery) => (
                            <div
                                key={`selected-${gallery.galleryId}`}
                                className="flex items-start gap-3 bg-white border border-[#681628] rounded-lg p-3 group"
                            >
                                <div className="w-16 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                                    <img
                                        src={gallery.photoUrl || defaultImage}
                                        alt={gallery.title}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h5 className="text-sm font-medium text-gray-900 truncate">{gallery.title}</h5>
                                    {(gallery.datePublished || gallery.publishedByName) ? (
                                        <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                                            {gallery.datePublished && (
                                                <p className="text-xs text-gray-500"><span className="font-medium">Published date:</span> {formatPublishedDate(gallery.datePublished)}</p>
                                            )}
                                            {gallery.publishedByName && (
                                                <p className="text-xs text-gray-500"><span className="font-medium">Published by:</span> {gallery.publishedByName}</p>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">Folder: {gallery.folderName}</p>
                                    )}
                                </div>
                                <button
                                    onClick={() => handleToggleSelect(gallery.galleryId)}
                                    className="p-1 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                                    title="Remove from selection"
                                >
                                    <X className="w-4 h-4 text-gray-500 group-hover:text-[#681628]" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="p-4 border-b border-gray-200">
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Search galleries"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full h-10 pl-4 pr-12 bg-gray-50 text-base font-normal text-gray-900 placeholder-gray-600 rounded border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#951F39] focus:border-transparent"
                    />
                    {searchQuery ? (
                        <button
                            type="button"
                            onClick={() => setSearchQuery('')}
                            className="absolute right-0 top-0 w-10 h-10 bg-[#951F39] rounded-r flex items-center justify-center hover:bg-[#801b31] transition-colors"
                        >
                            <CloseIcon className="w-5 h-5 text-white" />
                        </button>
                    ) : (
                        <div className="absolute right-0 top-0 w-10 h-10 bg-[#951F39] rounded-r flex items-center justify-center">
                            <SearchIcon className="w-5 h-5 text-white" />
                        </div>
                    )}
                </div>
            </div>

            <div className="max-h-[400px] overflow-y-auto p-4 space-y-3">
                {isLoading ? (
                    <div className="text-center py-8 text-gray-500">Loading galleries...</div>
                ) : galleries.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No more galleries available</div>
                ) : (
                    galleries.map((gallery) => (
                        <div
                            key={gallery.galleryId}
                            onClick={() => handleToggleSelect(gallery.galleryId)}
                            className="flex items-start gap-4 p-3 rounded-lg border cursor-pointer transition-all border-gray-200 hover:border-gray-300"
                        >
                            <div className="relative w-24 h-16 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                                <img
                                    src={gallery.photoUrl || defaultImage}
                                    alt={gallery.title}
                                    className="w-full h-full object-cover"
                                />
                            </div>

                            <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-gray-900 text-sm truncate">{gallery.title}</h4>
                                {(gallery.datePublished || gallery.publishedByName) && (
                                    <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                                        {gallery.datePublished && (
                                            <p className="text-xs text-gray-500"><span className="font-medium">Published date:</span> {formatPublishedDate(gallery.datePublished)}</p>
                                        )}
                                        {gallery.publishedByName && (
                                            <p className="text-xs text-gray-500"><span className="font-medium">Published by:</span> {gallery.publishedByName}</p>
                                        )}
                                    </div>
                                )}
                                <p className="text-xs text-gray-400 mt-1">Folder: {gallery.folderName}</p>
                            </div>

                            <div className="flex-shrink-0 pt-1">
                                <div className="w-6 h-6 rounded-full border border-gray-300" />
                            </div>
                        </div>
                    ))
                )}
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
                <Button variant="secondary" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={updateHighlightsMutation.isPending}
                >
                    {updateHighlightsMutation.isPending ? 'Saving...' : 'Save Selection'}
                </Button>
            </div>
        </div>
    );
};

export default InlinePhotoGalleryEditor;
